/**
 * useBehavioralTelemetry — global keystroke / cursor / focus sensor.
 *
 * Mounts document-level listeners for:
 *  • Keystroke flight time (keydown → keyup delta per key)
 *  • Deletion bursts (Backspace / Delete pressed ≥3× within 3 s)
 *  • Cursor jitter (velocity variance over a 500-ms window)
 *  • Focus changes (blur / focus)
 *  • Click cadence (delay between consecutive clicks)
 *
 * Events are debounced-batched and POSTed to /api/telemetry/events every
 * 30 s (or on beforeunload, whichever fires first). Payloads carry NO PII
 * — only timing/geometric metrics.
 *
 * Silent-fail: any network error is dropped locally; telemetry MUST NOT
 * degrade the user experience.
 */
import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

const FLUSH_INTERVAL_MS = 30_000;
const MAX_QUEUE = 500;

export default function useBehavioralTelemetry(enabled = true) {
    const queueRef       = useRef([]);
    const lastKeyDownRef = useRef({});  // key → ts
    const deletionRef    = useRef({ count: 0, windowStart: 0 });
    const cursorRef      = useRef({ samples: [], lastTs: 0 });
    const lastClickRef   = useRef(0);
    const flushingRef    = useRef(false);

    useEffect(() => {
        if (!enabled) return;
        if (typeof window === "undefined") return;

        const now = () => performance.now();
        const push = (ev) => {
            if (queueRef.current.length >= MAX_QUEUE) queueRef.current.shift();
            queueRef.current.push({ ...ev, client_ts: Date.now() });
        };

        // ── Keystroke flight time ──────────────────────────────────
        const onKeyDown = (e) => {
            // Skip modifier-only keys
            if (["Shift", "Control", "Alt", "Meta"].includes(e.key)) return;
            lastKeyDownRef.current[e.code] = now();

            // Deletion-burst detection
            if (e.key === "Backspace" || e.key === "Delete") {
                const t = now();
                const win = deletionRef.current;
                if (t - win.windowStart > 3000) {
                    // Emit if the previous window closed with ≥3 deletions.
                    if (win.count >= 3) {
                        push({ type: "deletion_burst", count: win.count, window_ms: 3000 });
                    }
                    win.windowStart = t;
                    win.count = 1;
                } else {
                    win.count += 1;
                }
            }
        };
        const onKeyUp = (e) => {
            const down = lastKeyDownRef.current[e.code];
            if (down) {
                const flight = now() - down;
                if (flight >= 0 && flight <= 2000) {
                    push({ type: "keystroke", flight_time_ms: flight });
                }
                delete lastKeyDownRef.current[e.code];
            }
        };

        // ── Cursor jitter ──────────────────────────────────────────
        // Sample every ~50 ms; every 500 ms compute velocity variance.
        const onMouseMove = (e) => {
            const t = now();
            const samples = cursorRef.current.samples;
            samples.push({ t, x: e.clientX, y: e.clientY });
            // Keep only the last 500 ms of samples.
            while (samples.length && t - samples[0].t > 500) samples.shift();
            if (t - cursorRef.current.lastTs > 500 && samples.length >= 4) {
                const velocities = [];
                for (let i = 1; i < samples.length; i++) {
                    const dt = samples[i].t - samples[i - 1].t;
                    if (dt <= 0) continue;
                    const dx = samples[i].x - samples[i - 1].x;
                    const dy = samples[i].y - samples[i - 1].y;
                    velocities.push(Math.sqrt(dx * dx + dy * dy) / dt);
                }
                if (velocities.length >= 2) {
                    const mean = velocities.reduce((a, b) => a + b, 0) / velocities.length;
                    const variance =
                        velocities.reduce((a, b) => a + (b - mean) ** 2, 0) / velocities.length;
                    if (variance > 0.01) {
                        push({ type: "cursor_jitter", velocity_variance: variance });
                    }
                }
                cursorRef.current.lastTs = t;
            }
        };

        // ── Click cadence ──────────────────────────────────────────
        const onClick = () => {
            const t = now();
            const prev = lastClickRef.current;
            if (prev > 0) {
                const delay = t - prev;
                if (delay >= 0 && delay < 60_000) {
                    push({ type: "click_cadence", delay_ms: delay });
                }
            }
            lastClickRef.current = t;
        };

        // ── Focus changes ──────────────────────────────────────────
        const onFocus = () => push({ type: "focus_change", state: "focus" });
        const onBlur  = () => push({ type: "focus_change", state: "blur"  });

        // ── Page view (SPA-friendly: fired once per mount, path=location.pathname) ──
        push({ type: "page_view", path: window.location.pathname, duration_ms: 0 });

        window.addEventListener("keydown",  onKeyDown, { passive: true });
        window.addEventListener("keyup",    onKeyUp,   { passive: true });
        window.addEventListener("mousemove", onMouseMove, { passive: true });
        window.addEventListener("click",    onClick,   { passive: true });
        window.addEventListener("focus",    onFocus,   { passive: true });
        window.addEventListener("blur",     onBlur,    { passive: true });

        // ── Flush loop ─────────────────────────────────────────────
        async function flush() {
            if (flushingRef.current) return;
            const events = queueRef.current.splice(0, MAX_QUEUE);
            if (!events.length) return;
            flushingRef.current = true;
            try {
                await api.post("/telemetry/events", { events });
            } catch (_e) {
                // Silent-fail: put back a tiny sample to try again next cycle.
                // (Full re-queue would grow unbounded when the API is down.)
            } finally {
                flushingRef.current = false;
            }
        }

        const flushId = setInterval(flush, FLUSH_INTERVAL_MS);
        const onBeforeUnload = () => {
            const events = queueRef.current.splice(0, MAX_QUEUE);
            if (!events.length) return;
            const body = JSON.stringify({ events });
            try {
                navigator.sendBeacon(
                    `${process.env.REACT_APP_BACKEND_URL}/api/telemetry/events`,
                    new Blob([body], { type: "application/json" }),
                );
            } catch (_e) { /* ignore */ }
        };
        window.addEventListener("beforeunload", onBeforeUnload);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup",   onKeyUp);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("click",   onClick);
            window.removeEventListener("focus",   onFocus);
            window.removeEventListener("blur",    onBlur);
            window.removeEventListener("beforeunload", onBeforeUnload);
            clearInterval(flushId);
            // One last best-effort flush on unmount.
            flush().catch(() => {});
        };
    }, [enabled]);
}
